import { useState, useEffect, useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  type User,
  type AdminReferralPartner,
  patchAdminUser,
  resetAdminUserStoreChangeLimit,
  resetAdminUserStoreChangeLimits,
  syncRetailersFromStripe,
  getAdminReferralPartners,
  setAdminUserReferral,
} from "../services/UserService";
import { Save, Check, X, RefreshCw, Plus, Ban, Unlink, Store, MapPin, ExternalLink, Loader2, Search, Pencil, History, ListPlus, RotateCcw, Users, Copy, ShieldCheck, Gift, MessageCircle, Bell, Percent, Activity, Beaker } from "lucide-react";
import GodModeActivator from "./GodModeActivator";
import AuditLogModal from "./AuditLogModal";
import UserPushTestCard from "./UserPushTestCard";
import StoreProductionHistoryPanel from "./adminTabs/StoreProductionHistoryPanel";
import {
  STORE_PRODUCTION_RETAILERS,
  type StoreProductionRetailer,
} from "../services/StoreProductionHistoryService";
import { useStores } from "../hooks/useStores";
import { geocodeZipCode } from "../utils/geocoding";
import { getActiveStoreChangeLimits, getLastStoreChangeForRetailer, hasUserRetailerAccess } from "../utils/userUtils";
import {
  getAllRetailers,
  getRetailerConfig,
  getRetailerDisplayName,
  getUserStoresForRetailer,
  prepareUserUpdateData,
  type RetailerType,
} from "../config/retailers";
import {
  RETAILERS_WITH_STORES,
  type RetailerWithStores,
} from "../utils/storesLoader";
import {
  buildAdminStoreOptions,
  normalizeAdminStoreIds,
  type AdminStoreOption,
} from "./AdminStorePicker.utils";
import { toast } from "react-toastify";
import { useUserContext } from "../context/UserContext";

const DiscordIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

interface UserDetailProps {
  user: User;
  onBack: () => void;
  onUserUpdated?: (updatedUser: User) => void;
}

type RetailerKey = RetailerWithStores;

const RETAILER_STYLES: Record<RetailerKey, { label: string; bg: string; text: string; lightBg: string }> = {
  walmart: { label: "Walmart", bg: "bg-blue-600", text: "text-blue-600 dark:text-blue-400", lightBg: "bg-blue-50 dark:bg-blue-900/20" },
  home_depot: { label: "Home Depot", bg: "bg-orange-500", text: "text-orange-600 dark:text-orange-400", lightBg: "bg-orange-50 dark:bg-orange-900/20" },
  target: { label: "Target", bg: "bg-red-500", text: "text-red-600 dark:text-red-400", lightBg: "bg-red-50 dark:bg-red-900/20" },
  dollar_general: { label: "Dollar General", bg: "bg-yellow-500", text: "text-yellow-600 dark:text-yellow-400", lightBg: "bg-yellow-50 dark:bg-yellow-900/20" },
  bestbuy: { label: "Best Buy", bg: "bg-indigo-500", text: "text-indigo-600 dark:text-indigo-400", lightBg: "bg-indigo-50 dark:bg-indigo-900/20" },
  costco: { label: "Costco", bg: "bg-rose-500", text: "text-rose-600 dark:text-rose-400", lightBg: "bg-rose-50 dark:bg-rose-900/20" },
  lowes: { label: "Lowe's", bg: "bg-cyan-600", text: "text-cyan-700 dark:text-cyan-400", lightBg: "bg-cyan-50 dark:bg-cyan-900/20" },
  tractor_supply: { label: "Tractor Supply", bg: "bg-red-700", text: "text-red-700 dark:text-red-400", lightBg: "bg-red-50 dark:bg-red-900/20" },
  northern_tool: { label: "Northern Tool", bg: "bg-red-600", text: "text-red-600 dark:text-red-400", lightBg: "bg-red-50 dark:bg-red-900/20" },
  samsclub: { label: "Sam's Club", bg: "bg-sky-700", text: "text-sky-700 dark:text-sky-400", lightBg: "bg-sky-50 dark:bg-sky-900/20" },
};

const STORE_CHANGE_TIMESTAMP_KEYS: Record<RetailerKey, keyof User> = {
  walmart: "lastStoreChange",
  home_depot: "lastHomeDepotStoreChange",
  target: "lastTargetStoreChange",
  dollar_general: "lastDollarGeneralStoreChange",
  bestbuy: "lastBestBuyStoreChange",
  costco: "lastCostcoStoreChange",
  lowes: "lastLowesStoreChange",
  tractor_supply: "lastTractorSupplyStoreChange",
  northern_tool: "lastNorthernToolStoreChange",
  samsclub: "lastSamsClubStoreChange",
};

const ADD_RETAILER_OPTIONS = [
  { value: "walmart_basic", label: "Walmart Basic" },
  { value: "walmart_starter", label: "Walmart Starter" },
  { value: "walmart_pro", label: "Walmart Pro" },
  { value: "home_depot_basic", label: "HD Basic" },
  { value: "home_depot_pro", label: "HD Pro" },
  { value: "target_basic", label: "Target Basic" },
  { value: "target_pro", label: "Target Pro" },
  { value: "dollar_general_basic", label: "Dollar General Basic" },
];

const ACCESS_RETAILERS = getAllRetailers().filter(
  (retailer) => retailer !== "walmart_online" && retailer !== "target_online",
);

const WALMART_PROVIDER_OPTIONS = [
  { value: "", label: "Default (global Dynamo config)" },
  { value: "walmartapp", label: "Walmart App" },
  { value: "tempo", label: "Tempo" },
  { value: "zephyr", label: "Zephyr" },
] as const;

const calcDist = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const dLat = ((lat2 - lat1) * Math.PI) / 180, dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 3959 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const formatStoreChangeTimestamp = (timestamp: number) => new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
}).format(new Date(timestamp));

/* ── Shared primitives ─────────────────────────────────── */

const Card = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <div className={`bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm ${className}`}>{children}</div>
);

const CardHeader = ({ icon, iconClass, title, subtitle, action }: {
  icon: ReactNode; iconClass: string; title: string; subtitle?: ReactNode; action?: ReactNode;
}) => (
  <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-3">
    <div className="flex items-center gap-2.5 min-w-0">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconClass}`}>{icon}</div>
      <div className="min-w-0 leading-tight">
        <h3 className="text-[13px] font-bold text-gray-900 dark:text-white">{title}</h3>
        {subtitle && <p className="text-[11px] text-gray-400 dark:text-dark-400 truncate">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

function Switch({ checked, busy, onChange, disabled }: { checked: boolean; busy?: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={busy || disabled}
      className={`relative w-9 h-5 rounded-full shrink-0 transition-colors disabled:opacity-60 ${checked ? "bg-emerald-500" : "bg-gray-200 dark:bg-dark-600"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow flex items-center justify-center transition-transform ${checked ? "translate-x-4" : ""}`}>
        {busy && <Loader2 className="w-2.5 h-2.5 animate-spin text-gray-400" />}
      </span>
    </button>
  );
}

function ToggleRow({ icon, title, description, checked, busy, onToggle }: {
  icon: ReactNode; title: string; description: string; checked: boolean; busy: boolean; onToggle: () => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span className="text-gray-400 dark:text-dark-400 shrink-0">{icon}</span>
      <div className="flex-1 min-w-0 leading-tight">
        <p className="text-[13px] font-medium text-gray-800 dark:text-dark-50">{title}</p>
        <p className="text-[11px] text-gray-400 dark:text-dark-400 truncate">{description}</p>
      </div>
      <Switch checked={checked} busy={busy} onChange={onToggle} />
    </div>
  );
}

/* ── Store editor modal ────────────────────────────────── */

function StoreModal({ user, retailer, storeIds, allStores, limit, onSave, onClose }: {
  user: User; retailer: RetailerKey; storeIds: string[]; allStores: AdminStoreOption[]; limit: number; onSave: (ids: string[]) => Promise<void>; onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>(() =>
    normalizeAdminStoreIds(storeIds, retailer),
  );
  const [saving, setSaving] = useState(false);
  const [zip, setZip] = useState(user.zipCode || "");
  const [coords, setCoords] = useState<{ lat: number; long: number } | null>(null);
  const style = RETAILER_STYLES[retailer];

  useEffect(() => {
    if (/^\d{5}$/.test(zip)) geocodeZipCode(zip).then(setCoords).catch(() => setCoords(null));
  }, [zip]);

  const stores = useMemo(() => {
    const list = allStores.map((s) => ({
      ...s,
      dist: coords && s.normalizedGeo
        ? calcDist(coords.lat, coords.long, s.normalizedGeo.lat, s.normalizedGeo.long)
        : undefined,
    }));
    list.sort((a, b) => (a.dist ?? Infinity) - (b.dist ?? Infinity));
    const q = search.toLowerCase();
    return list.filter((s) =>
      !q ||
      s.normalizedId.toLowerCase().includes(q) ||
      s.normalizedAddress.toLowerCase().includes(q) ||
      s.normalizedPostalCode.toLowerCase().includes(q)
    ).slice(0, 80);
  }, [allStores, coords, search]);

  const toggle = (id: string) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < limit ? [...prev, id] : prev);
  const save = async () => { setSaving(true); try { await onSave(selected); onClose(); } catch { toast.error("Failed"); } finally { setSaving(false); } };

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/40 dark:bg-black/60 backdrop-blur-[2px] flex items-end sm:items-center justify-center sheet-backdrop-in" onClick={onClose}>
      <div className="bg-white dark:bg-dark-800 w-full max-w-md max-h-[85vh] sm:max-h-[75vh] rounded-t-2xl sm:rounded-2xl flex flex-col shadow-2xl ring-1 ring-gray-200 dark:ring-dark-700 sheet-panel-in-sm" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 ${style.bg} rounded-lg flex items-center justify-center`}>
                <Store className="w-4 h-4 text-white" />
              </div>
              <div className="leading-tight">
                <h3 className="text-[13px] font-bold text-gray-900 dark:text-white">{style.label} stores</h3>
                <p className="text-[11px] text-gray-400 dark:text-dark-400 font-mono tabular-nums">{selected.length} / {limit} selected</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"><X className="w-4.5 h-4.5 text-gray-400 dark:text-dark-400" /></button>
          </div>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {selected.map((id) => (
                <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-dark-700 rounded-md text-xs font-mono font-medium text-gray-700 dark:text-dark-100">
                  #{id}
                  <button onClick={() => toggle(id)} className="hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="p-3 border-b border-gray-100 dark:border-dark-700 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search stores…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-[13px] rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-red-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-400 focus:outline-none transition-all" />
          </div>
          <div className="relative w-24">
            <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="ZIP" value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
              className="w-full pl-8 pr-2 py-2 text-[13px] font-mono rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-red-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-400 focus:outline-none transition-all" />
          </div>
        </div>

        {/* Store list */}
        <div className="flex-1 overflow-y-auto p-2 scrollbar-modern">
          {stores.length > 0 ? (
            <div className="space-y-0.5">
              {stores.map((s) => {
                const active = selected.includes(s.normalizedId), disabled = !active && selected.length >= limit;
                return (
                  <button key={s.normalizedId} onClick={() => !disabled && toggle(s.normalizedId)} disabled={disabled}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${active ? "bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-500/60" : disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-50 dark:hover:bg-dark-700"}`}>
                    <div className={`w-4.5 h-4.5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${active ? "border-emerald-600 bg-emerald-600" : "border-gray-300 dark:border-dark-500"}`}>
                      {active && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-mono font-bold ${style.text}`}>#{s.normalizedId}</span>
                        {s.dist !== undefined && <span className="text-[10px] font-mono text-gray-400 dark:text-dark-400">{s.dist.toFixed(1)} mi</span>}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-dark-300 truncate">{s.normalizedAddress || "Address unavailable"}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400 dark:text-dark-400">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No stores found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-100 dark:border-dark-700 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 text-[13px] font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 rounded-lg transition-colors">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 py-2 text-[13px] font-semibold text-white bg-gray-900 dark:bg-white dark:text-dark-900 hover:opacity-90 disabled:opacity-50 rounded-lg transition-opacity flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Per-retailer store card ───────────────────────────── */

function StoreCard({ user, retailer, storeIds, limit, lastStoreChange, resetting, resetDisabled, onUpdate, onReset }: {
  user: User;
  retailer: RetailerKey;
  storeIds: string[];
  limit: number;
  lastStoreChange: number | null;
  resetting: boolean;
  resetDisabled: boolean;
  onUpdate: (ids: string[]) => Promise<void>;
  onReset: () => Promise<void>;
}) {
  const [modal, setModal] = useState(false);
  const { stores, loading } = useStores(retailer);
  const style = RETAILER_STYLES[retailer];

  const allStores = useMemo<AdminStoreOption[]>(
    () => buildAdminStoreOptions(stores as unknown[] | null, retailer),
    [stores, retailer],
  );

  const normalizedStoreIds = useMemo(
    () => normalizeAdminStoreIds(storeIds, retailer),
    [storeIds, retailer],
  );
  const selectedStores = allStores.filter((s) => normalizedStoreIds.includes(s.normalizedId));

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-100 dark:border-dark-700">
        <div className="flex items-center gap-2.5">
          <span className={`w-2 h-2 rounded-full ${style.bg}`} />
          <span className="text-[13px] font-bold text-gray-900 dark:text-white">{style.label}</span>
          <span className="text-[11px] font-mono tabular-nums text-gray-400 dark:text-dark-400">{normalizedStoreIds.length}/{limit}</span>
        </div>
        <button onClick={() => setModal(true)} disabled={loading} className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-gray-500 dark:text-dark-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors disabled:opacity-50">
          <Pencil className="w-3 h-3" /> Edit
        </button>
      </div>
      <div className="p-2">
        {loading ? (
          <div className="flex items-center justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
        ) : normalizedStoreIds.length > 0 ? (
          <div className="space-y-0.5">
            {normalizedStoreIds.map((id) => {
              const store = selectedStores.find((s) => s.normalizedId === id);
              return (
                <div key={id} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700/50 text-xs transition-colors">
                  <span className={`font-mono font-bold shrink-0 ${style.text}`}>#{id}</span>
                  <span className="text-gray-500 dark:text-dark-300 truncate">{store?.normalizedAddress || "Not found"}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-gray-400 dark:text-dark-400 text-center py-3">No stores assigned</p>
        )}
      </div>
      {normalizedStoreIds.length > 0 && (
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 dark:border-dark-700 px-3.5 py-2.5">
          <div className="min-w-0 leading-tight">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-dark-400">Last store change</p>
            <p
              className="mt-0.5 truncate text-xs font-medium text-gray-600 dark:text-dark-200"
              title={lastStoreChange ? new Date(lastStoreChange).toLocaleString() : "No store change recorded"}
            >
              {lastStoreChange ? formatStoreChangeTimestamp(lastStoreChange) : "Never recorded"}
            </p>
          </div>
          {lastStoreChange && (
            <button
              type="button"
              onClick={onReset}
              disabled={resetDisabled}
              className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200 transition-colors hover:bg-amber-50 disabled:opacity-50 dark:text-amber-300 dark:ring-amber-900 dark:hover:bg-amber-900/20"
              title={`Clear ${style.label}'s store-change history so the user can change stores again`}
            >
              {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
              Reset
            </button>
          )}
        </div>
      )}
      {modal && <StoreModal user={user} retailer={retailer} storeIds={storeIds} allStores={allStores} limit={limit} onSave={onUpdate} onClose={() => setModal(false)} />}
    </Card>
  );
}

/* ── User detail ───────────────────────────────────────── */

export default function UserDetail({ user, onUserUpdated }: UserDetailProps) {
  const { user: currentUser } = useUserContext();
  const [detailTab, setDetailTab] = useState<"overview" | "production">("overview");
  const [retailers, setRetailers] = useState<string[]>(user.retailers || []);
  const [retailerAccessOverrides, setRetailerAccessOverrides] = useState<Record<string, boolean>>(user.retailerAccessOverrides || {});
  const [retailerAccessSearch, setRetailerAccessSearch] = useState("");
  const [savingAccessRetailer, setSavingAccessRetailer] = useState<string | null>(null);
  const [resettingRetailerAccess, setResettingRetailerAccess] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addValue, setAddValue] = useState("walmart_basic");
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pennyZip, setPennyZip] = useState(user.pennyZipCode || "");
  const [savingPennyZip, setSavingPennyZip] = useState(false);
  const [killModal, setKillModal] = useState(false);
  const [killing, setKilling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [togglingDM, setTogglingDM] = useState(false);
  const [togglingAllowAddLead, setTogglingAllowAddLead] = useState(false);
  const [togglingAllowMoreStores, setTogglingAllowMoreStores] = useState(false);
  const [togglingUnlimitedStoreChanges, setTogglingUnlimitedStoreChanges] = useState(false);
  const [togglingPennyBeta, setTogglingPennyBeta] = useState(false);
  const [togglingFreeTier, setTogglingFreeTier] = useState(false);
  const [togglingNext50Off, setTogglingNext50Off] = useState(false);
  const [maxAlerts, setMaxAlerts] = useState(user.maxAlertsOverride ? String(user.maxAlertsOverride) : "");
  const [savingMaxAlerts, setSavingMaxAlerts] = useState(false);
  const [stockCheckRadius, setStockCheckRadius] = useState(user.stockCheckRadiusOverride ? String(user.stockCheckRadiusOverride) : "");
  const [stockCheckStores, setStockCheckStores] = useState(user.stockCheckStoreLimitOverride ? String(user.stockCheckStoreLimitOverride) : "");
  const [savingStockCheck, setSavingStockCheck] = useState(false);
  const [walmartProviderOverride, setWalmartProviderOverride] = useState<NonNullable<User["walmartProviderOverride"]>>(user.walmartProviderOverride || "");
  const [savingWalmartProviderOverride, setSavingWalmartProviderOverride] = useState(false);
  const [resettingStoreLimits, setResettingStoreLimits] = useState(false);
  const [resettingStoreRetailer, setResettingStoreRetailer] = useState<RetailerKey | null>(null);
  const [auditModal, setAuditModal] = useState(false);
  const [referralPartners, setReferralPartners] = useState<AdminReferralPartner[]>([]);
  const [referralPartnersLoading, setReferralPartnersLoading] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(user.referralId || "");
  const [savingReferral, setSavingReferral] = useState(false);
  const [referralSearch, setReferralSearch] = useState("");

  const activeStoreChangeLimits = useMemo(() => getActiveStoreChangeLimits(user), [user]);

  useEffect(() => { setRetailers(user.retailers || []); }, [user.retailers]);
  useEffect(() => { setRetailerAccessOverrides(user.retailerAccessOverrides || {}); }, [user.retailerAccessOverrides]);
  useEffect(() => { setPennyZip(user.pennyZipCode || ""); }, [user.pennyZipCode]);
  useEffect(() => { setMaxAlerts(user.maxAlertsOverride ? String(user.maxAlertsOverride) : ""); }, [user.maxAlertsOverride]);
  useEffect(() => { setStockCheckRadius(user.stockCheckRadiusOverride ? String(user.stockCheckRadiusOverride) : ""); }, [user.stockCheckRadiusOverride]);
  useEffect(() => { setStockCheckStores(user.stockCheckStoreLimitOverride ? String(user.stockCheckStoreLimitOverride) : ""); }, [user.stockCheckStoreLimitOverride]);
  useEffect(() => { setWalmartProviderOverride(user.walmartProviderOverride || ""); }, [user.walmartProviderOverride]);
  useEffect(() => { setSelectedReferral(user.referralId || ""); }, [user.referralId]);

  useEffect(() => {
    let cancelled = false;
    setReferralPartnersLoading(true);
    getAdminReferralPartners()
      .then((partners) => { if (!cancelled) setReferralPartners(partners); })
      .catch(() => { if (!cancelled) toast.error("Failed to load referral partners"); })
      .finally(() => { if (!cancelled) setReferralPartnersLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const currentReferralPartner = useMemo(
    () => referralPartners.find((p) => p.ref.toLowerCase() === (user.referralId || "").toLowerCase()),
    [referralPartners, user.referralId],
  );

  const filteredReferralPartners = useMemo(() => {
    const q = referralSearch.trim().toLowerCase();
    const matches = !q
      ? referralPartners
      : referralPartners.filter((p) =>
          [p.ref, p.name, p.email, p.company].some((v) => (v || "").toLowerCase().includes(q)),
        );
    if (selectedReferral && !matches.some((p) => p.ref === selectedReferral)) {
      const selected = referralPartners.find((p) => p.ref === selectedReferral);
      if (selected) return [selected, ...matches];
    }
    return matches;
  }, [referralPartners, referralSearch, selectedReferral]);

  const changed = JSON.stringify([...retailers].sort()) !== JSON.stringify([...(user.retailers || [])].sort());
  const hasActiveSubscription = (user.retailers || []).length > 0;
  const formatRetailer = (r: string) => r.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  const filteredAccessRetailers = useMemo(() => {
    const query = retailerAccessSearch.trim().toLowerCase();
    if (!query) return ACCESS_RETAILERS;
    return ACCESS_RETAILERS.filter((retailer) =>
      retailer.includes(query) || getRetailerDisplayName(retailer).toLowerCase().includes(query),
    );
  }, [retailerAccessSearch]);
  const getStoreIds = (retailer: RetailerKey) => getUserStoresForRetailer(user, retailer);
  const actingAdminEmail = currentUser?.godModeOriginalUserId || currentUser?.userId || "";
  const canToggleFreeTier = actingAdminEmail.toLowerCase() === "info@dealhawkpro.io";

  const patchFlag = async (patch: Partial<User>, setBusy: (b: boolean) => void, successMsg: string) => {
    setBusy(true);
    try { await patchAdminUser(user.userId, patch); onUserUpdated?.({ ...user, ...patch }); toast.success(successMsg); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const saveRetailers = async () => {
    setSaving(true);
    try { const u = await patchAdminUser(user.userId, { retailers }); onUserUpdated?.(u); toast.success("Saved"); }
    catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const syncRetailers = async () => {
    setSyncing(true);
    try { const u = await syncRetailersFromStripe(user.userId); setRetailers(u.retailers || []); onUserUpdated?.(u); toast.success("Synced"); }
    catch { toast.error("Failed"); }
    finally { setSyncing(false); }
  };

  const inheritedRetailerAccess = (retailer: RetailerType) => hasUserRetailerAccess(
    { ...user, retailerAccessOverrides: undefined },
    retailer,
  );

  const effectiveRetailerAccess = (retailer: RetailerType) => hasUserRetailerAccess(
    { ...user, retailerAccessOverrides },
    retailer,
  );

  const toggleRetailerAccess = async (retailer: RetailerType) => {
    if (!hasActiveSubscription) return;
    const previous = retailerAccessOverrides;
    const nextAccess = !effectiveRetailerAccess(retailer);
    const next = { ...previous, [retailer]: nextAccess };
    if (nextAccess === inheritedRetailerAccess(retailer)) delete next[retailer];

    setRetailerAccessOverrides(next);
    setSavingAccessRetailer(retailer);
    try {
      const updated = await patchAdminUser(user.userId, { retailerAccessOverrides: next });
      setRetailerAccessOverrides(updated.retailerAccessOverrides || {});
      onUserUpdated?.(updated);
      toast.success(`${getRetailerDisplayName(retailer)} access ${nextAccess ? "enabled" : "disabled"}`);
    } catch {
      setRetailerAccessOverrides(previous);
      toast.error("Failed to update retailer access");
    } finally {
      setSavingAccessRetailer(null);
    }
  };

  const resetRetailerAccess = async () => {
    const previous = retailerAccessOverrides;
    setRetailerAccessOverrides({});
    setResettingRetailerAccess(true);
    try {
      const updated = await patchAdminUser(user.userId, { retailerAccessOverrides: {} });
      onUserUpdated?.(updated);
      toast.success("Retailer access reset to plan defaults");
    } catch {
      setRetailerAccessOverrides(previous);
      toast.error("Failed to reset retailer access");
    } finally {
      setResettingRetailerAccess(false);
    }
  };

  const updateStores = async (retailer: RetailerKey, ids: string[]) => {
    const data = {
      ...prepareUserUpdateData(retailer, { stores: ids }),
      [STORE_CHANGE_TIMESTAMP_KEYS[retailer]]: Date.now(),
    } as Partial<User>;
    const u = await patchAdminUser(user.userId, data);
    onUserUpdated?.(u);
    toast.success(`${RETAILER_STYLES[retailer].label} updated`);
  };

  const resetStoreChangeLimits = async () => {
    if (!activeStoreChangeLimits.length) {
      toast.info("No active store change limits to reset");
      return;
    }
    setResettingStoreLimits(true);
    try {
      const u = await resetAdminUserStoreChangeLimits(user.userId);
      onUserUpdated?.(u);
      toast.success("Store change limits reset");
    } catch {
      toast.error("Failed to reset store change limits");
    } finally {
      setResettingStoreLimits(false);
    }
  };

  const resetStoreChangeLimit = async (retailer: RetailerKey) => {
    setResettingStoreRetailer(retailer);
    try {
      const u = await resetAdminUserStoreChangeLimit(user.userId, retailer);
      onUserUpdated?.(u);
      toast.success(`${RETAILER_STYLES[retailer].label} store change reset`);
    } catch {
      toast.error(`Failed to reset ${RETAILER_STYLES[retailer].label}`);
    } finally {
      setResettingStoreRetailer(null);
    }
  };

  const maxAlertsDirty = (maxAlerts || "") !== (user.maxAlertsOverride ? String(user.maxAlertsOverride) : "");
  const defaultAlertLimit = user.accountType === "basic" ? 3 : 10;
  const saveMaxAlerts = async () => {
    const n = maxAlerts ? parseInt(maxAlerts, 10) : 0;
    if (maxAlerts && (!Number.isInteger(n) || n < 1)) {
      toast.error("Enter a positive number");
      return;
    }
    setSavingMaxAlerts(true);
    try {
      const u = await patchAdminUser(user.userId, { maxAlertsOverride: n });
      onUserUpdated?.(u);
      toast.success(n > 0 ? `Max alerts set to ${n}` : "Max alerts override cleared");
    } catch {
      toast.error("Failed");
    } finally {
      setSavingMaxAlerts(false);
    }
  };

  const stockCheckDirty =
    stockCheckRadius !== (user.stockCheckRadiusOverride ? String(user.stockCheckRadiusOverride) : "") ||
    stockCheckStores !== (user.stockCheckStoreLimitOverride ? String(user.stockCheckStoreLimitOverride) : "");
  const saveStockCheck = async () => {
    const miles = stockCheckRadius ? parseInt(stockCheckRadius, 10) : 0;
    const stores = stockCheckStores ? parseInt(stockCheckStores, 10) : 0;
    if (miles && (miles < 5 || miles > 200)) {
      toast.error("Radius must be 5–200 miles");
      return;
    }
    if (stores && stores > 50) {
      toast.error("Store count must be 1–50");
      return;
    }
    setSavingStockCheck(true);
    try {
      const u = await patchAdminUser(user.userId, { stockCheckRadiusOverride: miles, stockCheckStoreLimitOverride: stores });
      onUserUpdated?.(u);
      toast.success(miles || stores ? `Walmart stock check: ${miles ? `${miles} mi` : "default radius"}, ${stores ? `${stores} stores` : "default stores"}` : "Walmart stock check overrides cleared");
    } catch {
      toast.error("Failed to update Walmart stock check");
    } finally {
      setSavingStockCheck(false);
    }
  };

  const pennyZipDirty = (pennyZip || "") !== (user.pennyZipCode || "");

  const walmartProviderOverrideDirty = walmartProviderOverride !== (user.walmartProviderOverride || "");
  const saveWalmartProviderOverride = async () => {
    setSavingWalmartProviderOverride(true);
    try {
      const u = await patchAdminUser(user.userId, { walmartProviderOverride });
      onUserUpdated?.(u);
      toast.success(walmartProviderOverride ? `Walmart searches pinned to ${walmartProviderOverride}` : "Walmart provider override cleared");
    } catch {
      toast.error("Failed to update Walmart provider");
    } finally {
      setSavingWalmartProviderOverride(false);
    }
  };

  const showPennyZip = retailers.includes("home_depot_pro") || (user.retailers || []).includes("home_depot_pro");

  const savePennyZip = async () => {
    if (pennyZip && !/^\d{5}$/.test(pennyZip)) {
      toast.error("Please enter a valid 5-digit zip code");
      return;
    }
    setSavingPennyZip(true);
    try {
      const u = await patchAdminUser(user.userId, { pennyZipCode: pennyZip || "" });
      onUserUpdated?.(u);
      toast.success("Penny zip updated");
    } catch {
      toast.error("Failed");
    } finally {
      setSavingPennyZip(false);
    }
  };

  const referralDirty = (selectedReferral || "") !== (user.referralId || "");
  const saveReferral = async () => {
    if (!selectedReferral) {
      toast.error("Select a referral partner");
      return;
    }
    setSavingReferral(true);
    try {
      const { user: u } = await setAdminUserReferral(user.userId, selectedReferral);
      onUserUpdated?.(u);
      toast.success(user.referralId ? "Referral updated" : "Referral set");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to set referral");
    } finally {
      setSavingReferral(false);
    }
  };

  const killUser = async () => {
    setKilling(true);
    try { const { killUser: kill } = await import("../services/UserService"); await kill(user.userId); toast.success("User killed"); onUserUpdated?.({ ...user, banned: true, retailers: [] }); setKillModal(false); }
    catch { toast.error("Failed"); }
    finally { setKilling(false); }
  };

  const copyUserId = async () => {
    try { await navigator.clipboard.writeText(user.userId); toast.success("Email copied!"); } catch { /* noop */ }
  };

  const dynamoDbUrl = `https://us-east-2.console.aws.amazon.com/dynamodbv2/home?region=us-east-2#edit-item?itemMode=2&pk=${encodeURIComponent(user.userId)}&route=ROUTE_ITEM_EXPLORER&table=users`;
  const productionStoreIds = useMemo(
    () => Object.fromEntries(
      STORE_PRODUCTION_RETAILERS.map((retailer) => [retailer, getUserStoresForRetailer(user, retailer)]),
    ) as Partial<Record<StoreProductionRetailer, string[]>>,
    [user],
  );

  return (
    <div>
      <div className="mb-4 flex w-fit items-center gap-1 rounded-xl bg-gray-100 p-1 dark:bg-dark-800" role="tablist" aria-label="User detail sections">
        <button
          type="button"
          role="tab"
          aria-selected={detailTab === "overview"}
          onClick={() => setDetailTab("overview")}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${detailTab === "overview" ? "bg-white text-gray-950 shadow-sm dark:bg-dark-700 dark:text-white" : "text-gray-500 hover:text-gray-900 dark:text-dark-300 dark:hover:text-white"}`}
        >
          <Users className="h-3.5 w-3.5" /> Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={detailTab === "production"}
          onClick={() => setDetailTab("production")}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${detailTab === "production" ? "bg-white text-gray-950 shadow-sm dark:bg-dark-700 dark:text-white" : "text-gray-500 hover:text-gray-900 dark:text-dark-300 dark:hover:text-white"}`}
        >
          <Activity className="h-3.5 w-3.5" /> Store production
        </button>
      </div>

      {detailTab === "production" ? (
        <StoreProductionHistoryPanel mode="user" storeIdsByRetailer={productionStoreIds} userId={user.userId} />
      ) : (
      <div className="space-y-4">

      {/* ── Identity header ───────────────────────────── */}
      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className={`text-lg font-bold tracking-tight ${user.banned ? "text-gray-400 dark:text-dark-400 line-through" : "text-gray-900 dark:text-white"}`}>{user.name || "Unknown"}</h2>
              {user.banned && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-600 text-white"><Ban className="w-3 h-3" /> Banned</span>
              )}
              {user.verified ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-900/25 dark:text-emerald-300 dark:ring-emerald-800"><ShieldCheck className="w-3 h-3" /> Verified</span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500 dark:bg-dark-700 dark:text-dark-300">Unverified</span>
              )}
              {user.discordUserId && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200 dark:bg-indigo-900/25 dark:text-indigo-300 dark:ring-indigo-800"><DiscordIcon size={10} /> Linked</span>
              )}
              {user.freeTierEnabled && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-900/25 dark:text-amber-300 dark:ring-amber-800"><Gift className="w-3 h-3" /> Free tier</span>
              )}
              {user.next50OffCouponPending && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200 dark:bg-purple-900/25 dark:text-purple-300 dark:ring-purple-800"><Percent className="w-3 h-3" /> 50% off pending</span>
              )}
            </div>

            <button onClick={copyUserId} className="group flex items-center gap-1.5 mt-0.5 min-w-0" title="Copy email">
              <span className="text-[13px] font-mono text-gray-500 dark:text-dark-300 truncate">{user.userId}</span>
              <Copy className="w-3 h-3 shrink-0 text-gray-300 dark:text-dark-500 group-hover:text-gray-500 dark:group-hover:text-dark-300 transition-colors" />
            </button>

            <div className="flex items-center gap-4 mt-2 flex-wrap text-[11px] text-gray-400 dark:text-dark-400">
              <span>Created <span className="font-mono font-semibold text-gray-600 dark:text-dark-200">{user.created ? new Date(user.created).toLocaleDateString() : "—"}</span></span>
              <span>Last active <span className="font-mono font-semibold text-gray-600 dark:text-dark-200" title={user.lastActive ? new Date(user.lastActive).toLocaleString() : undefined}>{user.lastActive ? new Date(user.lastActive).toLocaleString() : "Never"}</span></span>
              {user.toltCustomerId && <span className="truncate max-w-[240px]" title={user.toltCustomerId}>Tolt <span className="font-mono text-gray-500 dark:text-dark-300">{user.toltCustomerId}</span></span>}
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <GodModeActivator targetUser={user} />
            <a
              href={dynamoDbUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
            >
              <ExternalLink className="w-3 h-3" /> DynamoDB
            </a>
            {user.customerId && (
              <a
                href={`https://dashboard.stripe.com/customers/${user.customerId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                <ExternalLink className="w-3 h-3" /> Stripe
              </a>
            )}
            <button onClick={() => setAuditModal(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
              <History className="w-3 h-3" /> Audit log
            </button>
            {!user.banned && (
              <button onClick={() => setKillModal(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-900 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Cancel subscription, remove plans and ban">
                <Ban className="w-3 h-3" /> Kill
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* ── Plans / retailers ─────────────────────────── */}
      <Card>
        <CardHeader
          icon={<Store className="w-4 h-4 text-gray-600 dark:text-dark-200" />}
          iconClass="bg-gray-100 dark:bg-dark-700"
          title="Plans"
          subtitle={changed ? "Unsaved changes" : `${retailers.length} active plan${retailers.length === 1 ? "" : "s"}`}
          action={
            <div className="flex items-center gap-1.5">
              {user.customerId && (
                <button onClick={syncRetailers} disabled={syncing} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors disabled:opacity-50">
                  <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} /> Sync Stripe
                </button>
              )}
              <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
                <Plus className="w-3 h-3" /> Add plan
              </button>
            </div>
          }
        />
        <div className="px-4 pb-4">
          {showAdd && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 dark:bg-dark-700/60 rounded-xl">
              <select value={addValue} onChange={(e) => setAddValue(e.target.value)} className="text-xs px-2.5 py-2 rounded-lg bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-600 text-gray-900 dark:text-white flex-1 focus:outline-none focus:ring-2 focus:ring-red-500/60">
                {ADD_RETAILER_OPTIONS.filter((o) => !retailers.includes(o.value)).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={() => { if (!retailers.includes(addValue)) setRetailers([...retailers, addValue]); setShowAdd(false); }} className="text-xs px-3 py-2 bg-gray-900 dark:bg-white text-white dark:text-dark-900 rounded-lg font-semibold hover:opacity-90 transition-opacity">Add</button>
              <button onClick={() => setShowAdd(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-dark-100 transition-colors"><X className="w-4 h-4" /></button>
            </div>
          )}
          {retailers.length > 0 || (user.retailers || []).some((r) => !retailers.includes(r)) ? (
            <div className="flex flex-wrap gap-1.5">
              {retailers.map((r) => {
                const isNew = !(user.retailers || []).includes(r);
                return (
                  <span key={r} className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg text-xs font-semibold ring-1 ring-inset ${isNew ? "bg-emerald-50 text-emerald-700 ring-emerald-300 dark:bg-emerald-900/25 dark:text-emerald-300 dark:ring-emerald-700" : "bg-gray-50 text-gray-700 ring-gray-200 dark:bg-dark-700 dark:text-dark-100 dark:ring-dark-600"}`}>
                    {formatRetailer(r)}
                    {isNew && <span className="text-[9px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">new</span>}
                    <button onClick={() => setRetailers(retailers.filter((x) => x !== r))} className="p-0.5 rounded hover:text-red-500 transition-colors"><X className="w-3 h-3" /></button>
                  </span>
                );
              })}
              {(user.retailers || []).filter((r) => !retailers.includes(r)).map((r) => (
                <span key={`rm-${r}`} className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-400 dark:text-red-400 line-through">
                  {formatRetailer(r)}
                </span>
              ))}
            </div>
          ) : <p className="text-xs text-gray-400 dark:text-dark-400">No plans assigned</p>}

          {changed && (
            <div className="flex items-center justify-between gap-3 mt-3 px-3 py-2.5 bg-amber-50 dark:bg-amber-900/15 ring-1 ring-amber-200 dark:ring-amber-900 rounded-xl">
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Unsaved plan changes</span>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setRetailers(user.retailers || [])} className="text-xs px-2.5 py-1.5 rounded-lg font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors">Discard</button>
                <button onClick={saveRetailers} disabled={saving} className="text-xs px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-dark-900 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-1.5">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* ── Per-user retailer access ─────────────────── */}
      <Card>
        <CardHeader
          icon={<ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
          iconClass="bg-emerald-50 dark:bg-emerald-900/25"
          title="Retailer access"
          subtitle="Override this user's plan without changing their subscription"
          action={Object.keys(retailerAccessOverrides).length > 0 ? (
            <button
              type="button"
              onClick={resetRetailerAccess}
              disabled={resettingRetailerAccess || savingAccessRetailer !== null}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-dark-300 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
            >
              {resettingRetailerAccess ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Use plan defaults
            </button>
          ) : undefined}
        />
        <div className="px-4 pb-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="search"
              value={retailerAccessSearch}
              onChange={(event) => setRetailerAccessSearch(event.target.value)}
              placeholder="Search retailers…"
              aria-label="Search retailer access"
              className="w-full pl-8 pr-3 py-2 text-xs rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-emerald-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none transition-all"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-1.5 max-h-80 overflow-y-auto pr-1 scrollbar-modern">
            {filteredAccessRetailers.map((retailer) => {
              const override = retailerAccessOverrides[retailer];
              const enabled = effectiveRetailerAccess(retailer);
              const status = !hasActiveSubscription
                ? override === undefined ? "No plan" : "Inactive"
                : override === true ? "Granted" : override === false ? "Blocked" : "Plan";
              return (
                <div key={retailer} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 bg-gray-50/80 dark:bg-dark-700/40 ring-1 ring-inset ring-gray-100 dark:ring-dark-700">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-gray-800 dark:text-dark-50">{getRetailerDisplayName(retailer)}</p>
                    <p className={`mt-0.5 text-[10px] font-bold uppercase tracking-wide ${override === true ? "text-emerald-600 dark:text-emerald-400" : override === false ? "text-red-500 dark:text-red-400" : "text-gray-400 dark:text-dark-400"}`}>
                      {status}
                    </p>
                  </div>
                  <Switch
                    checked={enabled}
                    busy={savingAccessRetailer === retailer}
                    disabled={!hasActiveSubscription || resettingRetailerAccess || (savingAccessRetailer !== null && savingAccessRetailer !== retailer)}
                    onChange={() => toggleRetailerAccess(retailer)}
                  />
                </div>
              );
            })}
          </div>
          {filteredAccessRetailers.length === 0 && (
            <p className="py-8 text-center text-xs text-gray-400 dark:text-dark-400">No retailers match that search.</p>
          )}
          <p className="mt-3 text-[11px] leading-relaxed text-gray-400 dark:text-dark-400">
            {hasActiveSubscription
              ? "Plan means access is inherited from billing. Granted and Blocked are user-specific exceptions; Walmart Online follows Walmart, and Target Online follows Target."
              : "Retailer overrides require an active subscription. Saved exceptions are inactive until the user resubscribes."}
          </p>
        </div>
      </Card>

      {/* ── Access & referral row ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Access & flags */}
        <Card className="overflow-hidden">
          <CardHeader
            icon={<ShieldCheck className="w-4 h-4 text-gray-600 dark:text-dark-200" />}
            iconClass="bg-gray-100 dark:bg-dark-700"
            title="Access & flags"
          />
          <div className="divide-y divide-gray-50 dark:divide-dark-700/60 pb-1.5">
            <ToggleRow
              icon={<Check className="w-4 h-4" />}
              title="Verified"
              description="Account email is verified"
              checked={!!user.verified}
              busy={verifying}
              onToggle={() => patchFlag({ verified: !user.verified }, setVerifying, user.verified ? "Unverified" : "Verified")}
            />
            {canToggleFreeTier && (
              <ToggleRow
                icon={<Gift className="w-4 h-4" />}
                title="Free tier"
                description="Grant access without a subscription"
                checked={!!user.freeTierEnabled}
                busy={togglingFreeTier}
                onToggle={() => patchFlag({ freeTierEnabled: !user.freeTierEnabled }, setTogglingFreeTier, user.freeTierEnabled ? "Free tier disabled" : "Free tier enabled")}
              />
            )}
            <ToggleRow
              icon={<Percent className="w-4 h-4" />}
              title="50% off next subscription"
              description="Apply a 50% off first-month coupon at their next Stripe checkout"
              checked={!!user.next50OffCouponPending}
              busy={togglingNext50Off}
              onToggle={() => patchFlag({ next50OffCouponPending: !user.next50OffCouponPending }, setTogglingNext50Off, user.next50OffCouponPending ? "50% off grant removed" : "50% off will apply at next checkout")}
            />
            <ToggleRow
              icon={<Store className="w-4 h-4" />}
              title="Extra store slots"
              description="Show and edit stores above the plan max"
              checked={!!user.allowMoreStores}
              busy={togglingAllowMoreStores}
              onToggle={() => patchFlag({ allowMoreStores: !user.allowMoreStores }, setTogglingAllowMoreStores, user.allowMoreStores ? "Allow more stores off" : "Allow more stores on")}
            />
            <ToggleRow
              icon={<Store className="w-4 h-4" />}
              title="Unlimited store changes"
              description="Skip the once-per-24-hours store change limit"
              checked={!!user.unlimitedStoreChanges}
              busy={togglingUnlimitedStoreChanges}
              onToggle={() => patchFlag({ unlimitedStoreChanges: !user.unlimitedStoreChanges }, setTogglingUnlimitedStoreChanges, user.unlimitedStoreChanges ? "Unlimited store changes off" : "Unlimited store changes on")}
            />
            <ToggleRow
              icon={<Beaker className="w-4 h-4" />}
              title="Penny Beta"
              description={user.admin ? "Admins always have access; this flag controls the stored rollout state" : "Allow access to the experimental Home Depot penny candidate feed"}
              checked={!!user.pennyBeta}
              busy={togglingPennyBeta}
              onToggle={() => patchFlag({ pennyBeta: !user.pennyBeta }, setTogglingPennyBeta, user.pennyBeta ? "Penny Beta disabled" : "Penny Beta enabled")}
            />
            <div className="flex items-center gap-3 px-4 py-2.5">
              <span className="text-gray-400 dark:text-dark-400 shrink-0"><Bell className="w-4 h-4" /></span>
              <div className="flex-1 min-w-0 leading-tight">
                <p className="text-[13px] font-medium text-gray-800 dark:text-dark-50">Max alerts</p>
                <p className="text-[11px] text-gray-400 dark:text-dark-400 truncate">
                  Raise the alert limit above the plan default ({defaultAlertLimit}). Empty = plan default.
                </p>
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={maxAlerts}
                onChange={(e) => setMaxAlerts(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder={String(defaultAlertLimit)}
                className="w-16 px-2.5 py-1.5 text-[13px] font-mono text-center rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-emerald-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-400 focus:outline-none transition-all"
              />
              <button
                onClick={saveMaxAlerts}
                disabled={savingMaxAlerts || !maxAlertsDirty}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity shrink-0"
              >
                {savingMaxAlerts ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            </div>
            <div className="flex items-center gap-3 px-4 py-2.5">
              <span className="text-gray-400 dark:text-dark-400 shrink-0"><MapPin className="w-4 h-4" /></span>
              <div className="flex-1 min-w-0 leading-tight">
                <p className="text-[13px] font-medium text-gray-800 dark:text-dark-50">Walmart stock check reach</p>
                <p className="text-[11px] text-gray-400 dark:text-dark-400 truncate">
                  Radius (mi, 5–200) and closest stores checked (max 50). Empty = global default.
                </p>
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                aria-label="Stock check radius in miles"
                value={stockCheckRadius}
                onChange={(e) => setStockCheckRadius(e.target.value.replace(/\D/g, "").slice(0, 3))}
                placeholder="mi"
                className="w-14 px-2 py-1.5 text-[13px] font-mono text-center rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-emerald-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-400 focus:outline-none transition-all"
              />
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                aria-label="Stock check store count"
                value={stockCheckStores}
                onChange={(e) => setStockCheckStores(e.target.value.replace(/\D/g, "").slice(0, 2))}
                placeholder="25"
                className="w-14 px-2 py-1.5 text-[13px] font-mono text-center rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-emerald-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-400 focus:outline-none transition-all"
              />
              <button
                onClick={saveStockCheck}
                disabled={savingStockCheck || !stockCheckDirty}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity shrink-0"
              >
                {savingStockCheck ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            </div>
            <div className="flex items-center gap-3 px-4 py-2.5">
              <span className="text-gray-400 dark:text-dark-400 shrink-0"><Search className="w-4 h-4" /></span>
              <div className="flex-1 min-w-0 leading-tight">
                <p className="text-[13px] font-medium text-gray-800 dark:text-dark-50">Walmart search provider</p>
                <p className="text-[11px] text-gray-400 dark:text-dark-400 truncate">
                  Pin this user to one provider. Default follows the global Dynamo priority.
                </p>
              </div>
              <select
                aria-label="Walmart search provider"
                value={walmartProviderOverride}
                onChange={(e) => setWalmartProviderOverride(e.target.value as NonNullable<User["walmartProviderOverride"]>)}
                disabled={savingWalmartProviderOverride}
                className="max-w-44 px-2.5 py-1.5 text-xs rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-gray-200 dark:ring-dark-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/60"
              >
                {WALMART_PROVIDER_OPTIONS.map((provider) => (
                  <option key={provider.value || "default"} value={provider.value}>{provider.label}</option>
                ))}
              </select>
              <button
                onClick={saveWalmartProviderOverride}
                disabled={savingWalmartProviderOverride || !walmartProviderOverrideDirty}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity shrink-0"
              >
                {savingWalmartProviderOverride ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </button>
            </div>
            <ToggleRow
              icon={<ListPlus className="w-4 h-4" />}
              title="Add leads"
              description="User can submit new leads"
              checked={!!user.allowAddLead}
              busy={togglingAllowAddLead}
              onToggle={() => patchFlag({ allowAddLead: !user.allowAddLead }, setTogglingAllowAddLead, user.allowAddLead ? "Add lead disabled" : "Add lead enabled")}
            />
            {user.discordUserId && (
              <>
                <ToggleRow
                  icon={<MessageCircle className="w-4 h-4" />}
                  title="Discord DMs"
                  description="Send deal alerts via Discord DM"
                  checked={!!user.allowSendToDiscord}
                  busy={togglingDM}
                  onToggle={() => patchFlag({ allowSendToDiscord: !user.allowSendToDiscord }, setTogglingDM, "Updated")}
                />
                <div className="flex items-center gap-3 px-4 py-2.5">
                  <span className="text-indigo-400 shrink-0"><DiscordIcon size={16} /></span>
                  <div className="flex-1 min-w-0 leading-tight">
                    <p className="text-[13px] font-medium text-gray-800 dark:text-dark-50">Discord</p>
                    <p className="text-[11px] font-mono text-gray-400 dark:text-dark-400 truncate">{user.discordUserId}</p>
                  </div>
                  <button
                    onClick={async () => { setUnlinking(true); try { await patchAdminUser(user.userId, { discordUserId: "" }); onUserUpdated?.({ ...user, discordUserId: "" }); toast.success("Unlinked"); } catch { toast.error("Failed"); } finally { setUnlinking(false); } }}
                    disabled={unlinking}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-dark-300 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
                  >
                    {unlinking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />} Unlink
                  </button>
                </div>
              </>
            )}
            {user.discordUrl && (
              <div className="px-4 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400 mb-1">Discord webhook</p>
                <p className="font-mono text-[11px] text-gray-500 dark:text-dark-300 truncate" title={user.discordUrl}>{user.discordUrl}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Referral + penny ZIP */}
        <div className="space-y-4">
          <Card>
            <CardHeader
              icon={<Users className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
              iconClass="bg-emerald-50 dark:bg-emerald-900/25"
              title="Referral"
              subtitle={user.referralId
                ? <>Current: <span className="font-semibold text-gray-600 dark:text-dark-200">{currentReferralPartner?.name || user.referralId}</span> <span className="font-mono">({user.referralId})</span></>
                : "No referral partner set"}
              action={
                <button
                  onClick={saveReferral}
                  disabled={savingReferral || !referralDirty || !selectedReferral}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity shrink-0"
                >
                  {savingReferral ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  {user.referralId ? "Switch" : "Set"}
                </button>
              }
            />
            <div className="px-4 pb-4 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={referralSearch}
                  onChange={(e) => setReferralSearch(e.target.value)}
                  placeholder="Search partners…"
                  className="w-full pl-8 pr-3 py-2 text-xs rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-emerald-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none transition-all"
                />
              </div>
              {referralPartnersLoading ? (
                <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
              ) : (
                <select
                  value={selectedReferral}
                  onChange={(e) => setSelectedReferral(e.target.value)}
                  className="w-full text-xs px-2.5 py-2 rounded-lg bg-white dark:bg-dark-700 ring-1 ring-gray-200 dark:ring-dark-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/60"
                >
                  <option value="">{user.referralId ? "Keep current / choose partner…" : "Select referral partner…"}</option>
                  {filteredReferralPartners.map((p) => (
                    <option key={p.ref} value={p.ref}>
                      {p.name || p.ref} — {p.ref}{p.email ? ` (${p.email})` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </Card>

          {showPennyZip && (
            <Card>
              <CardHeader
                icon={<MapPin className="w-4 h-4 text-orange-500 dark:text-orange-400" />}
                iconClass="bg-orange-50 dark:bg-orange-900/25"
                title="Penny Items Pro ZIP"
                subtitle={user.pennyZipCode ? <>Currently <span className="font-mono font-semibold text-gray-600 dark:text-dark-200">{user.pennyZipCode}</span></> : "Not set"}
                action={
                  <button
                    onClick={savePennyZip}
                    disabled={savingPennyZip || !pennyZipDirty}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity shrink-0"
                  >
                    {savingPennyZip ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                  </button>
                }
              />
              <div className="px-4 pb-4 flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={5}
                  value={pennyZip}
                  onChange={(e) => setPennyZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="ZIP code"
                  className="flex-1 px-3 py-2 text-[13px] font-mono tracking-widest rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-orange-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder:tracking-normal placeholder-gray-400 focus:outline-none transition-all"
                />
                <button
                  onClick={() => setPennyZip("")}
                  disabled={!pennyZip}
                  className="px-3 py-2 rounded-lg text-xs font-semibold text-gray-500 dark:text-dark-300 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-40 transition-colors"
                >
                  Clear
                </button>
              </div>
            </Card>
          )}
        </div>
      </div>

      <UserPushTestCard
        key={`${user.userId}:${JSON.stringify(user.storeIds)}:${!!user.banned}`}
        userId={user.userId}
        storeIds={user.storeIds || []}
        banned={user.banned}
      />

      {/* ── Stores ────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2.5 px-1">
          <h3 className="text-[13px] font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" /> Stores
          </h3>
          {activeStoreChangeLimits.length > 0 && (
            <button
              type="button"
              onClick={resetStoreChangeLimits}
              disabled={resettingStoreLimits || resettingStoreRetailer !== null}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-900 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors disabled:opacity-50"
              title="Clear today's store-change limits so the user can pick stores again"
            >
              {resettingStoreLimits ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
              Reset store limits
            </button>
          )}
        </div>
        {activeStoreChangeLimits.length > 0 && (
          <p className="mb-2.5 px-1 text-xs text-amber-700 dark:text-amber-300">
            Active limits: {activeStoreChangeLimits.map((r) => getRetailerConfig(r).displayName).join(", ")}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {RETAILERS_WITH_STORES.map((retailer) => (
            <StoreCard
              key={retailer}
              user={user}
              retailer={retailer}
              storeIds={getStoreIds(retailer)}
              limit={999}
              lastStoreChange={getLastStoreChangeForRetailer(user, retailer)}
              resetting={resettingStoreRetailer === retailer}
              resetDisabled={resettingStoreLimits || resettingStoreRetailer !== null}
              onUpdate={(ids) => updateStores(retailer, ids)}
              onReset={() => resetStoreChangeLimit(retailer)}
            />
          ))}
        </div>
      </div>

      {/* ── Kill modal ────────────────────────────────── */}
      {killModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-[2px] flex items-center justify-center z-50 p-4" onClick={() => setKillModal(false)}>
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl ring-1 ring-gray-200 dark:ring-dark-700 max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/25 flex items-center justify-center"><Ban className="text-red-600 dark:text-red-400" size={20} /></div>
              <div className="leading-tight">
                <h2 className="font-bold text-gray-900 dark:text-white">Kill user</h2>
                <p className="text-xs text-gray-400 dark:text-dark-400">This action cannot be undone</p>
              </div>
            </div>
            <ul className="text-[13px] text-gray-600 dark:text-dark-200 space-y-1.5 mb-5">
              <li className="flex items-center gap-2"><X size={14} className="text-red-500 shrink-0" />Cancel subscription</li>
              <li className="flex items-center gap-2"><X size={14} className="text-red-500 shrink-0" />Remove all plans</li>
              <li className="flex items-center gap-2"><X size={14} className="text-red-500 shrink-0" />Ban the user</li>
            </ul>
            <div className="flex gap-2">
              <button onClick={() => setKillModal(false)} disabled={killing} className="flex-1 py-2 text-[13px] font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 rounded-lg transition-colors">Cancel</button>
              <button onClick={killUser} disabled={killing} className="flex-1 py-2 text-[13px] font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                {killing ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />} {killing ? "Killing…" : "Kill user"}
              </button>
            </div>
          </div>
        </div>
      )}
      {auditModal && <AuditLogModal userId={user.userId} onClose={() => setAuditModal(false)} />}
      </div>
      )}
    </div>
  );
}
